import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { appTypography } from '../theme/appTypography';
import {
    Box,
    Typography,
    Button,
    Avatar,
    ButtonBase,
    ThemeProvider,
    createTheme,
    styled,
    useTheme,
    useMediaQuery,
    Container,
} from '@mui/material';
import {
    People as PeopleIcon,
    Business as BusinessIcon,
    ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';

// THEME
const inquiryFlowTheme = createTheme({
    palette: {
        primary: { main: '#2db838', contrastText: '#fff', light: 'rgba(45, 184, 56, 0.1)' },
        secondary: { main: '#0288d1' },
        background: { default: '#f4f7f9', paper: '#FFFFFF' },
        text: { primary: '#3A3B3C', secondary: '#5A5A5A' },
    },
    typography: {
        fontFamily: "'Inter', sans-serif",
        h4: { fontWeight: 700 },
        h5: { fontWeight: 600 },
        button: { textTransform: 'none', fontWeight: 600, borderRadius: 8 },
        ...appTypography,
    },
    shape: { borderRadius: 12 },
});

// Inject CSS animations once
const shakeKeyframes = `
@keyframes shake {
  10%, 90% { transform: translateX(-2px); }
  20%, 80% { transform: translateX(4px); }
  30%, 50%, 70% { transform: translateX(-8px); }
  40%, 60% { transform: translateX(8px); }
}
@keyframes floatInOut {
  0% { transform: scale(1) translateZ(0px); z-index: 1; filter: brightness(1); }
  10% { transform: scale(1.10) translateZ(30px); z-index: 2; filter: brightness(1.07);}
  50% { transform: scale(1) translateZ(0px); z-index: 1; filter: brightness(1);}
  60% { transform: scale(1.10) translateZ(30px); z-index: 2; filter: brightness(1.07);}
  100% { transform: scale(1) translateZ(0px); z-index: 1; filter: brightness(1);}
}`;
if (typeof window !== "undefined" && !document.getElementById('inquiry-shake-keyframes')) {
    const style = document.createElement('style');
    style.id = 'inquiry-shake-keyframes';
    style.innerHTML = shakeKeyframes;
    document.head.appendChild(style);
}

// Styled card with shaking & floating
// FIX 1: Updated motion() to motion.create() for newer Framer Motion versions
const AnimatedChoiceCard = styled(motion.create(ButtonBase), {
    shouldForwardProp: (prop) => prop !== 'shaking' && prop !== 'float',
})(({ theme, shaking, float }) => ({
    width: '100%',
    maxWidth: 420,
    padding: theme.spacing(4),
    borderRadius: theme.shape.borderRadius,
    border: `1.5px solid ${theme.palette.divider}`,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    backgroundColor: theme.palette.background.paper,
    transition: 'all 0.3s ease',
    boxShadow: float ? theme.shadows[8] : theme.shadows[1],
    zIndex: float ? 2 : 1,
    transform: float ? 'scale(1.05)' : 'scale(1)',
    animation: shaking ? 'shake 0.5s ease' : float ? 'floatInOut 3s ease infinite' : 'none',
    '&:hover': {
        cursor: 'pointer',
    },
}));

export default function InquiryLandingPage() {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTabletOrLarger = useMediaQuery(theme.breakpoints.up('sm'));

    const [activeTab, setActiveTab] = useState(0);
    const [shaking, setShaking] = useState([false, false]);
    const animTimeoutRef = useRef();

    useEffect(() => {
        animTimeoutRef.current = setTimeout(() => {
            setActiveTab((prev) => (prev === 0 ? 1 : 0));
        }, 4000);
        return () => clearTimeout(animTimeoutRef.current);
    }, [activeTab]);

    const handleClick = (index, route) => {
        setShaking((prev) => prev.map((_, i) => i === index));
        setTimeout(() => navigate(route), 300); // Shake before navigating
    };

    const options = [
        {
            title: 'I am a Client',
            description: 'Looking for travel, visa, or other services.',
            icon: <PeopleIcon fontSize="large" />,
            route: '/new-inquiry',
        },
        {
            title: 'I am a Service Provider',
            description: 'Looking to partner or list your services with us.',
            icon: <BusinessIcon fontSize="large" />,
            route: '/service-provider-inquiry',
        },
    ];

    return (
        <ThemeProvider theme={inquiryFlowTheme}>
            <Box sx={{ height: '100vh', overflowY: 'auto', bgcolor: 'background.default', py: 4 }}>
                <Container maxWidth="md">
                    <Box
                        component="img"
                        src="/pics/logo.png"
                        alt="Abisiniya Logo"
                        sx={{
                            width: { xs: 90, sm: 120, md: 140 },
                            height: { xs: 90, sm: 120, md: 140 },
                            objectFit: 'contain',
                            mb: 2,
                            mx: 'auto',
                            display: 'block',
                        }}
                    />
                    <Typography
                        variant="h4"
                        component="h1"
                        sx={{
                            fontWeight: 700,
                            letterSpacing: 1.2,
                            textAlign: 'center',
                            mb: 1,
                            fontSize: { xs: 22, sm: 28 },
                        }}
                    >
                        Welcome To The Abisiniya Inquiry System
                    </Typography>
                    <Typography
                        variant="h6"
                        color="text.secondary"
                        sx={{
                            mb: 5,
                            textAlign: 'center',
                            fontSize: { xs: 16, sm: 20 },
                        }}
                    >
                        How would you like to inquire today?
                    </Typography>

                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: isMobile ? 'column' : 'row',
                            gap: isTabletOrLarger ? 4 : 3,
                            justifyContent: 'center',
                            alignItems: 'stretch',
                        }}
                    >
                        {options.map((opt, index) => (
                            <AnimatedChoiceCard
                                key={index}
                                shaking={shaking[index]}
                                float={activeTab === index}
                                onClick={() => handleClick(index, opt.route)}
                                whileHover={{ scale: 1.07 }}
                                // FIX 2: Render as a div so it doesn't conflict with the inner Button
                                component="div" 
                                sx={{
                                    minHeight: isTabletOrLarger ? 350 : 240,
                                    px: isTabletOrLarger ? 4 : 2,
                                    py: isTabletOrLarger ? 4 : 3,
                                    mx: 'auto',
                                }}
                            >
                                <Avatar
                                    sx={{
                                        bgcolor: 'primary.main',
                                        width: { xs: 56, sm: 68 },
                                        height: { xs: 56, sm: 68 },
                                        mb: 2,
                                        boxShadow: 3,
                                    }}
                                >
                                    {opt.icon}
                                </Avatar>
                                <Typography
                                    variant="h5"
                                    component="h2"
                                    gutterBottom
                                    sx={{
                                        fontWeight: 700,
                                        fontSize: { xs: 20, sm: 24 },
                                        color: activeTab === index ? 'primary.main' : 'text.primary',
                                    }}
                                >
                                    {opt.title}
                                </Typography>
                                <Typography
                                    color="text.secondary"
                                    sx={{ mb: 2, flexGrow: 1, fontSize: { xs: 14, sm: 16 } }}
                                >
                                    {opt.description}
                                </Typography>
                                <Button
                                    variant="contained"
                                    endIcon={<ArrowForwardIcon />}
                                    fullWidth={isMobile}
                                    sx={{
                                        fontWeight: 600,
                                        fontSize: { xs: 14, sm: 16 },
                                        boxShadow: 2,
                                    }}
                                >
                                    {index === 0 ? 'Start Client Inquiry' : 'Start Provider Inquiry'}
                                </Button>
                            </AnimatedChoiceCard>
                        ))}
                    </Box>
                </Container>
            </Box>
        </ThemeProvider>
    );
}